class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.searchButton = this.querySelector('[data-search-submit]');
    this.form = this.querySelector('form[role="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.merchandising = this.querySelector('[data-search-merchandising]');
    this.recentSearchesSection = this.querySelector('[data-recent-searches-section]');
    this.recentSearchesList = this.querySelector('[data-recent-searches-list]');
    this.storageKey = 'diazzaRecentSearches';
    this.abortController = null;

    if (!this.input || !this.form || !this.predictiveSearchResults) return;

    this.input.addEventListener('focusin', () => {
      if (this.input.value.trim() !== '' && this.predictiveSearchResults.innerHTML.trim() !== '') {
        this.open();
      } else {
        this.showMerchandising();
      }
    });

    this.input.addEventListener('input', this.debounce(() => {
      this.onChange();
    }, 250).bind(this));

    // Keep Search/Enter inside the drawer. The full search page is only opened
    // by the explicit View All link rendered below the six product cards.
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.runDrawerSearch();
    });

    if (this.searchButton) {
      this.searchButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.runDrawerSearch();
      });
    }

    this.addEventListener('click', (event) => {
      const clearButton = event.target.closest('[data-clear-recent-searches]');
      if (clearButton) {
        event.preventDefault();
        this.clearRecentSearches();
        return;
      }

      // Popular/recent searches should populate this drawer, not navigate away.
      const termLink = event.target.closest('[data-search-term]');
      if (termLink && !termLink.classList.contains('diazza-predictive-view-all')) {
        event.preventDefault();
        const term = termLink.dataset.searchTerm || termLink.textContent || '';
        this.input.value = term.trim();
        this.saveSearchTerm(term);
        this.runDrawerSearch();
        return;
      }

      const resultLink = event.target.closest('#predictive-search a');
      if (resultLink) this.saveSearchTerm(this.input.value);
    });

    this.renderRecentSearches();
    this.showMerchandising();
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      if (this.abortController) this.abortController.abort();
      this.predictiveSearchResults.innerHTML = '';
      this.close();
      this.showMerchandising();
      return;
    }

    this.hideMerchandising();
    this.getSearchResults(searchTerm);
  }

  runDrawerSearch() {
    const searchTerm = this.input.value.trim();
    if (!searchTerm) {
      this.close();
      this.showMerchandising();
      this.input.focus();
      return;
    }

    this.saveSearchTerm(searchTerm);
    this.hideMerchandising();
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('section_id', 'predictive-search');
    params.set('resources[type]', 'product');
    // Fetch a seventh product only so Liquid can know whether View All is needed.
    // The rendered drawer itself is hard-capped at six cards in predictive-search.liquid.
    params.set('resources[limit]', '7');

    fetch(`/search/suggest?${params.toString()}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        if (!section) return;

        this.predictiveSearchResults.innerHTML = section.innerHTML;
        this.open();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close();
        console.error('Predictive search error:', error);
      });
  }

  open() {
    this.hideMerchandising();
    this.predictiveSearchResults.classList.add('open');
    this.predictiveSearchResults.classList.remove('hidden');
    this.predictiveSearchResults.setAttribute('aria-expanded', 'true');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.predictiveSearchResults.classList.remove('open');
    this.predictiveSearchResults.classList.add('hidden');
    this.predictiveSearchResults.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-expanded', 'false');

    if (!this.input.value.trim()) this.showMerchandising();
  }

  showMerchandising() {
    if (this.merchandising) this.merchandising.classList.remove('hidden');
  }

  hideMerchandising() {
    if (this.merchandising) this.merchandising.classList.add('hidden');
  }

  getRecentSearches() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
      return Array.isArray(saved) ? saved.filter((item) => typeof item === 'string' && item.trim()) : [];
    } catch (error) {
      return [];
    }
  }

  saveSearchTerm(term) {
    const cleanTerm = (term || '').trim();
    if (!cleanTerm) return;

    try {
      const existing = this.getRecentSearches();
      const withoutDuplicate = existing.filter((item) => item.toLowerCase() !== cleanTerm.toLowerCase());
      window.localStorage.setItem(this.storageKey, JSON.stringify([cleanTerm, ...withoutDuplicate].slice(0, 5)));
      this.renderRecentSearches();
    } catch (error) {
      // Search should still work when localStorage is unavailable.
    }
  }

  clearRecentSearches() {
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (error) {
      // Ignore storage errors.
    }
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    if (!this.recentSearchesSection || !this.recentSearchesList) return;

    const searches = this.getRecentSearches();
    this.recentSearchesList.innerHTML = '';

    if (!searches.length) {
      this.recentSearchesSection.classList.add('hidden');
      return;
    }

    searches.forEach((term) => {
      const link = document.createElement('a');
      link.href = `${this.form.action}?q=${encodeURIComponent(term)}`;
      link.className = 'diazza-search-chip';
      link.dataset.searchTerm = term;
      link.textContent = term;
      this.recentSearchesList.appendChild(link);
    });

    this.recentSearchesSection.classList.remove('hidden');
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
